import { AddOutlined, DeleteOutlined, EditOutlined } from '@mui/icons-material';
import { Button, Skeleton, Stack, Typography } from '@mui/material';

import { BillingScheduleBar, BillingScheduleChips } from './billingSchedule';
import { SectionCard } from 'components/v2/common/sectionCard';

import { ACTIONS } from 'constant';
import useTranslate from 'hooks/useTranslate';
import type { BillingModalData, BillingRule } from 'types';
import { formatCurrency } from 'utils/formatters';

interface DefaultRuleCardProps {
    loading: boolean;
    rule: BillingRule | null;
    servicesOnDefault: number;
    servicesTotal: number;
    handleAction: (action: ACTIONS, data: BillingModalData) => void;
}

const DefaultRuleCard = ({
    loading,
    rule,
    servicesOnDefault,
    servicesTotal,
    handleAction,
}: DefaultRuleCardProps) => {
    const { translate } = useTranslate();

    const actions = loading ? null : (
        <>
            {rule ? (
                <Button
                    size="small"
                    color="inherit"
                    startIcon={<DeleteOutlined />}
                    onClick={() => handleAction(ACTIONS.DELETE, { target: 'default' })}
                    sx={{ textTransform: 'none' }}
                >
                    {translate('serviceProviderBilling.removeDefault')}
                </Button>
            ) : null}
            <Button
                size="small"
                variant={rule ? 'outlined' : 'contained'}
                startIcon={rule ? <EditOutlined /> : <AddOutlined />}
                onClick={() => handleAction(ACTIONS.EDIT, { target: 'default' })}
                sx={{ textTransform: 'none' }}
            >
                {translate(
                    rule
                        ? 'serviceProviderBilling.editDefault'
                        : 'serviceProviderBilling.setDefault'
                )}
            </Button>
        </>
    );

    const content = (() => {
        if (loading) {
            return (
                <Stack spacing={1.5}>
                    <Skeleton variant="text" width={180} height={40} />
                    <Skeleton variant="rounded" height={6} />
                    <Skeleton variant="rounded" width="55%" height={24} />
                </Stack>
            );
        }
        if (!rule) {
            return (
                <Typography variant="body2" color="text.secondary">
                    {translate('serviceProviderBilling.defaultRule.empty')}
                </Typography>
            );
        }
        return (
            <Stack spacing={1.5}>
                <Stack direction="row" spacing={1} sx={{ alignItems: 'baseline' }}>
                    <Typography variant="h5" sx={{ fontWeight: 700 }}>
                        {formatCurrency(rule.price, rule.currency)}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        {translate('serviceProviderBilling.perCase')}
                    </Typography>
                </Stack>
                <BillingScheduleBar installments={rule.installments} />
                <BillingScheduleChips rule={rule} />
                <Typography variant="body2" color="text.secondary">
                    {`${servicesOnDefault}/${servicesTotal} ${translate(
                        'serviceProviderBilling.defaultRule.servicesUsing'
                    )}`}
                </Typography>
            </Stack>
        );
    })();

    return (
        <SectionCard
            title={translate('serviceProviderBilling.defaultRule.title')}
            actions={actions}
        >
            {content}
        </SectionCard>
    );
};

export default DefaultRuleCard;
